// import { Box, Flex, HStack, Text } from '@chakra-ui/layout';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
  ManagementPage,
  ManagementPageProps,
} from '../ManagementPage/ManagementPage';
import {
  ContractEnvironment,
  ContractStatus,
  ICertificateProject
} from '@mentaport/certificates-projects';
// import { Button } from '@chakra-ui/button';

import { useDialog } from '../../../hooks/use-dialog';
import { shouldBeLoading } from '../../../helpers/contract-list-helper';
// import { Image } from '@chakra-ui/image';
import { LocalIcons } from '../../../assets/icons/icons';
import { MessageStatus } from '../../../services/dialog';
import { Tab, TabList, TabPanel, TabPanels, Tabs, Button, Image ,  Box, Flex, HStack,Tooltip } from '@chakra-ui/react';
import { DataTable, TableColumn } from '../Table/Table';
import { CustomCellRendererProps } from 'ag-grid-react';
// import { useNavigate } from 'react-router-dom';
// import { authentication } from '../../../services/authentication';

export interface ProjectListComponentProps {
  managementPageProps: ManagementPageProps;
  environments: ProjectEnvironmentContent[];
  complexActivationLoading?: boolean;
  onEditProject: (project: ICertificateProject) => void;
  onPauseProject: (project: ICertificateProject) => Promise<void>;
  onResumeProject: (project: ICertificateProject) => Promise<void>;
  onActivateProject: (project: ICertificateProject) => Promise<void>;
  onShowContractAnalytics: (contractId: string, projectId:string, contractName: string) => void;
}
export interface ProjectEnvironmentContent {
  environmentType: ContractEnvironment;
  projects: ICertificateProject[];
}

enum ProjectActions {
  Activate = 'activate',
  Pause = 'pause',
  Resume = 'resume',
  Download = 'download',
}

export const ProjectListComponent: FunctionComponent<
  ProjectListComponentProps
> = ({ managementPageProps,
  environments,
  complexActivationLoading,
  onEditProject,
  onPauseProject,
  onResumeProject,
  onActivateProject,
  onShowContractAnalytics
}) => {
    const [asyncActionIsRunning, setAsyncActionIsRunning] =
      useState<boolean>(false);
    const [focusedContractId, setFocusOnContractId] = useState<string>('');
    const [activeAction, setActiveAction] = useState<ProjectActions | undefined>();
    const dialog = useDialog();
    // const navigate = useNavigate();
    const [currentComplexActivationLoading, setComplexActivationLoading] = useState(
      complexActivationLoading
    );

    useEffect(() => {
      setComplexActivationLoading(complexActivationLoading);
    }, [complexActivationLoading]);

    const EnvironmentTable: React.FC<{ environment: ProjectEnvironmentContent }> = ({ environment }) => {
      const { projects, environmentType } = environment;

      const renderBlockchainIcon = (blockchain: string) => {
        const iconMap: any = {
          ethereum: LocalIcons.EthereumLogo,
          base: LocalIcons.BaseLogo,
          monad: LocalIcons.MonadLogo,
          soneium: LocalIcons.SoneiumLogo,
          polygon: LocalIcons.PolygonLogo,
          sui: LocalIcons.SuiLogo,
          none: LocalIcons.MentaportIcon,
        };
        return (
          <Tooltip title={blockchain}>
          <Image
            margin="0 auto"
            borderRadius={100}
            width={'1.5rem'}
            height={'1.5rem'}
            src={iconMap[blockchain]}
            alt={blockchain}
          />
          </Tooltip>
        );
      };

      const renderActionButtons = (props: CustomCellRendererProps) => {
        const project = props.data as ICertificateProject;
        return (
          <HStack>
            {renderStatus(project)}
            {project.status !== ContractStatus.Archive &&
              <HStack>
                {renderLifecycleProjectTools(project)}
                <Button
                  onClick={() => onEditProject(project)}
                >
                  Edit
                </Button>

                <Button
                  disabled={true}
                  onClick={() => {
                    onShowContractAnalytics(
                      project.contractId,
                      project.projectId,
                      project.projectName
                    );
                  }}
                >
                  Analytics
                </Button>
              </HStack>
            }
          </HStack>
        );
      }

      const tableColumns: TableColumn[] = useMemo(() => {
        let tableColumns: TableColumn[] = [];
        if (projects?.length) {
          const nonMezzanineColumns = ['blockchain', 'address'];
          const keysToInclude = ['projectName', 'customerId']
          const totalKeysToInclude = ['contractId','projectId', ...(environmentType !== ContractEnvironment.Mezzanine ? nonMezzanineColumns : []), ...keysToInclude];
          totalKeysToInclude.forEach((key) => {
            // key is camelCase, so we separate the words with a space and capitalize the first letter
            const title = key
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str) => str.toUpperCase());
            tableColumns.push({
              title,
              id: key,
              width: environmentType !== ContractEnvironment.Mezzanine ? 140 : undefined,
            });
          });

          // if (environmentType === ContractEnvironment.Mezzanine) {
          //   const columnsToRemove = ['blockchain', 'address', 'projectId'];
          //   tableColumns = tableColumns.filter(
          //     (column) => !columnsToRemove.includes(column.id)
          //   );
          // }

          tableColumns.push({
            title: 'Actions',
            id: 'actions',
            width: 380,
            customRenderer: (props) => renderActionButtons(props),
          });

          const blockchainColumn = tableColumns.find(
            (column) => column.id === 'blockchain'
          );
          if (blockchainColumn) {
            blockchainColumn.customRenderer = (props) => {
              return (
                <Flex
                  justifyContent={'center'}
                  alignItems={'center'}
                  width={'100%'}
                >
                  {renderBlockchainIcon(props.value)}
                </Flex>
              );
            };
          }
        }
        return tableColumns;
      }, [
        environmentType,
        projects,
      ])

      return (
        <DataTable columns={tableColumns} data={projects} rowHeight={60} height={1000} getRowStyle={() => ({
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
        })} />
      )
    };

    const handleAsyncAction = async (
      project: ICertificateProject,
      action?: (project: ICertificateProject) => Promise<void> | undefined
    ) => {
      if (!action) {
        return;
      }
      try {
        setAsyncActionIsRunning(true);
        setFocusOnContractId(project.contractId);
        await action(project);
      } catch (error: any) {
        dialog.notify(
          MessageStatus.Error,
          `${project.environment === ContractEnvironment.Mainnet
            ? 'Mezzanine'
            : 'Blockchain'
          } Contracts`,
          error?.data.message ?? 'There was an error during thee process'
        );
      } finally {
        setAsyncActionIsRunning(false);
      }
    };

    const renderLifecycleProjectTools = (project: ICertificateProject) => {

      return (
        <>
          {project.status === ContractStatus.NonActive && (
            <Button
              isLoading={
                shouldBeLoading(
                  project.contractId,
                  focusedContractId,
                  asyncActionIsRunning,
                  activeAction === ProjectActions.Activate
                ) || complexActivationLoading
              }
              key="btn-pause"
              width={10}
              cursor={'pointer'}
              variant="orange"
              aria-label="Activate"
              onClick={() => {
                console.log("clicked")
                setActiveAction(ProjectActions.Activate);
                handleAsyncAction(project, () =>
                  onActivateProject(project)
                );
              }}
            >
              <Image
                margin="0 auto"
                borderRadius={0}
                objectFit={'cover'}
                width={'1rem'}
                height={'1rem'}
                src={LocalIcons.Launch}
                alt={'Pause'}
              />
            </Button>
          )}

          {project.status === ContractStatus.Active && (

            <Button
              isLoading={shouldBeLoading(
                project.contractId,
                focusedContractId,
                asyncActionIsRunning,
                activeAction === ProjectActions.Pause
              )}
              key="btn-pause"
              width={10}
              cursor={'pointer'}
              onClick={() => {
                setActiveAction(ProjectActions.Pause);
                handleAsyncAction(project, () => {
                  return onPauseProject(project);
                });
              }}
            >
              <Image
                margin="0 auto"
                borderRadius={0}
                width={'1rem'}
                height={'1rem'}
                src={LocalIcons.Pause}
                alt={'Pause'}
              />
            </Button>
          )}
          {project.status === ContractStatus.Paused && (
            <Button
              isLoading={shouldBeLoading(
                project.contractId,
                focusedContractId,
                asyncActionIsRunning,
                activeAction === ProjectActions.Resume
              )}
              key="btn-resume"
              width={10}
              cursor={'pointer'}
              onClick={() => {
                setActiveAction(ProjectActions.Resume);
                handleAsyncAction(project, () => {
                  return onResumeProject(project);
                });
              }}
            >
              <Image
                margin="0 auto"
                borderRadius={0}
                width={'1rem'}
                height={'1rem'}
                src={LocalIcons.Play}
                alt={'Resume'}
              />
            </Button>
          )}
          {project.status === ContractStatus.Archive && (
            <Button
              isActive={false}
              key="btn-resume"
              width={10}

            >
              <Image
                margin="0 auto"
                borderRadius={0}
                width={'1rem'}
                height={'1rem'}
                src={LocalIcons.Play}
                alt={'Resume'}
              />
            </Button>
          )}
        </>
      );
    };

    const renderStatus = (project: ICertificateProject) => {
      let variantName = '';
      let label = '';
      let color = 'black'
      if (project.status === ContractStatus.NonActive) {
        label = 'Inactive';
        variantName = 'black';
        color = '#ddd'
      } else if (project.status === ContractStatus.Active) {
        label = 'Active';
        variantName = 'mint';
      } else if (project.status === ContractStatus.Paused) {
        label = 'Paused';
        variantName = 'orange';
      } else if (project.status === ContractStatus.Pending) {
        label = 'Pending';
        variantName = 'yellow';
      } else if (project.status === ContractStatus.Archive) {
        label = 'Archive';
        variantName = 'red';
        color = '#ddd'
      }

      return (
        <Button key="status" width={100} cursor={'default'} variant={variantName} color={color}>
          {label}
        </Button>
      );
    };

    return (
      <Flex flexDirection="column" height={'100%'} width="100%">
        <ManagementPage
          title={managementPageProps.title}
          buttons={managementPageProps.buttons}
          path={managementPageProps.path}
          hideBackButton={managementPageProps.hideBackButton}
          isLoading={managementPageProps.isLoading}
          placeholder={managementPageProps.placeholder}
          breadcrumbs={managementPageProps.breadcrumbs}
          previousLocation={managementPageProps.previousLocation}
        >
          <Tabs colorScheme="black" overflowY={'scroll'}>
            <TabList>
              {environments.map((environment, index) => {
                if (environment.environmentType !== ContractEnvironment.Localhost) {
                  return (
                    <Tab key={index} fontSize={'large'}>
                      {environment?.environmentType.toString().toUpperCase()}
                    </Tab>
                  );
                }
              })}
            </TabList>
            <TabPanels>

              {environments.map((environment, index) => {
                return (
                  <TabPanel key={index}>
                    <Box key={environment.environmentType}>
                      <EnvironmentTable environment={environment} />
                    </Box>
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        </ManagementPage>
      </Flex>
    );
  };
